import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import {
  acceptInvitation,
  createUserInvitation,
  deleteUserInvitation,
  getInvitationInfo,
  resendUserInvitation,
} from '../invitations'
import { queryKeys } from '../react-query-config'
import type {
  AcceptInvitationDTO,
  AcceptInvitationResponseDTO,
  ActivateUserResponseDto,
  CreateUserInvitationDTO,
  CreateUserInvitationResponse,
  GetInvitationInfoResponseDTO,
  GetUsersResponse,
  ResendUserInvitationResponse,
  SetupUserDriveResponseDto,
  SuspendUserResponseDto,
  TransferPrimaryAdminDto,
  TransferPrimaryAdminResponseDto,
  UpdateProfileDto,
  UpdateProfileResponseDto,
  UserFilters,
} from '../types'
import {
  activateUser,
  getUsers,
  setupUserDrive,
  suspendUser,
  transferPrimaryAdmin,
  updateUserProfile,
} from '../user-management'

// Create user invitation hook
export const useCreateUserInvitation = () => {
  return useMutation<CreateUserInvitationResponse, Error, CreateUserInvitationDTO>({
    mutationFn: createUserInvitation,
    retry: false,
  })
}

// Resend user invitation hook
export const useResendUserInvitation = () => {
  const queryClient = useQueryClient()

  return useMutation<ResendUserInvitationResponse, Error, string>({
    mutationFn: resendUserInvitation,
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
    },
  })
}

// Delete user invitation hook
export const useDeleteUserInvitation = () => {
  const queryClient = useQueryClient()

  return useMutation<void, Error, string>({
    mutationFn: deleteUserInvitation,
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
    },
  })
}

export const useGetUsers = (
  filters: UserFilters,
  options?: {
    enabled?: boolean
    tenantStatus?: string
  },
) => {
  const { enabled, tenantStatus, ...restOptions } = options || {}

  // Check if tenant status prevents API calls
  const isTenantStatusBlocked =
    tenantStatus && ['incomplete', 'suspended', 'cancelled'].includes(tenantStatus)

  return useQuery<GetUsersResponse, Error>({
    queryKey: queryKeys.users.list(filters),
    queryFn: () => {
      // Double-check the blocking condition inside the query function
      if (isTenantStatusBlocked) {
        // Return a rejected promise to prevent the API call
        return Promise.reject(new Error('API call blocked due to tenant status'))
      }

      return getUsers(filters)
    },
    enabled: enabled !== false && !isTenantStatusBlocked, // Default to true if not specified, but block if tenant status is restricted
    ...restOptions,
  })
}

export const useGetInvitationInfo = (tenantId: string, invitationCode: string) => {
  return useQuery<GetInvitationInfoResponseDTO, Error>({
    queryKey: ['invitation-info', tenantId, invitationCode],
    queryFn: () => getInvitationInfo(tenantId, invitationCode),
    enabled: !!tenantId && !!invitationCode,
  })
}

// Accept invitation hook
export const useAcceptInvitation = () => {
  return useMutation<
    AcceptInvitationResponseDTO,
    Error,
    { tenantId: string; invitationCode: string; data: AcceptInvitationDTO }
  >({
    mutationFn: ({ tenantId, invitationCode, data }) =>
      acceptInvitation(tenantId, invitationCode, data),
    retry: false,
  })
}

// Suspend user hook
export const useSuspendUser = () => {
  const queryClient = useQueryClient()

  return useMutation<SuspendUserResponseDto, Error, string>({
    mutationFn: suspendUser,
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
    },
  })
}

// Activate user hook
export const useActivateUser = () => {
  const queryClient = useQueryClient()

  return useMutation<ActivateUserResponseDto, Error, string>({
    mutationFn: activateUser,
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
    },
  })
}

export const useUpdateUserProfile = () => {
  const queryClient = useQueryClient()

  return useMutation<
    UpdateProfileResponseDto,
    Error,
    {
      userId: string
      data: UpdateProfileDto
    }
  >({
    mutationFn: ({ userId, data }) => updateUserProfile(userId, data),
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
    },
  })
}

// Transfer primary admin hook
export const useTransferPrimaryAdmin = () => {
  const queryClient = useQueryClient()

  return useMutation<TransferPrimaryAdminResponseDto, Error, TransferPrimaryAdminDto>({
    mutationFn: transferPrimaryAdmin,
    retry: false,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.users.all })
    },
  })
}

// Setup user drive hook
export const useSetupUserDrive = () => {
  const queryClient = useQueryClient()

  return useMutation<SetupUserDriveResponseDto, Error, string>({
    mutationFn: setupUserDrive,
    retry: false,
    onSuccess: () => {
      // Invalidate user data to refresh drive status
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.me() })
    },
  })
}
